import type { ProductGroupsData } from '@/entities/optimization-group'
import type {
	OptimizationGroupProductGroupMapping,
	SalesLevelModel,
} from '@/shared/generated'
import type { TreeNodeType } from '@/shared/lib/tree'

import React, { useMemo } from 'react'
import { uniq } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { findNodes, getStatisticsByLevels } from '@/shared/lib/tree'

type State = {
	selectedSalesEntities: string[]
	selectedProductGroups: OptimizationGroupProductGroupMapping[]
}
type CheckDataViewProps = {
	state: State
	salesLevels?: SalesLevelModel[]
	salesEntitiesTree: TreeNodeType[]
	productGroups?: ProductGroupsData
}
type Summary = { name: string; statistics: { name: string }[] }

const categoryIdsOf = (
	entry: OptimizationGroupProductGroupMapping
): string[] => {
	const rule = entry.filters?.find((filter) => filter.name === 'category_id')
	if (!rule || !('value' in rule) || !Array.isArray(rule.value)) return []

	return rule.value.map(String)
}

export default function CheckDataView({
	state,
	salesLevels,
	salesEntitiesTree,
	productGroups,
}: CheckDataViewProps) {
	const { t } = useTranslation()

	const salesEntitiesSummary = useMemo(() => {
		const statistics = getStatisticsByLevels(
			findNodes(salesEntitiesTree, state.selectedSalesEntities)
		)

		return (salesLevels ?? [])
			.map((salesLevel) => ({
				name: salesLevel.name,
				statistics: statistics[salesLevel.level] || [],
			}))
			.filter((item) => item.statistics.length)
	}, [salesEntitiesTree, salesLevels, state.selectedSalesEntities])

	const productGroupsSummary = useMemo((): Summary[] => {
		if (!productGroups) return []
		const { catalog } = productGroups
		const groups = state.selectedProductGroups
			.map((entry) => catalog.groups.get(entry.product_group_id))
			.filter((group): group is NonNullable<typeof group> => Boolean(group))
		const categories = uniq(state.selectedProductGroups.flatMap(categoryIdsOf))
			.map((id) => catalog.catalog.dataOf.get(id))
			.filter((category): category is NonNullable<typeof category> =>
				Boolean(category)
			)

		return [
			{ name: t('product_groups'), statistics: groups },
			{
				name: t('field_schema_categories', { ns: 'fields' }),
				statistics: categories,
			},
		]
	}, [productGroups, state.selectedProductGroups, t])

	return (
		<div className='relative flex flex-col overflow-y-auto'>
			<div className='grid gap-2 text-sm'>
				<div>
					<span className='font-medium'>{t('sales_entities')}</span>
					<SummaryList summary={salesEntitiesSummary} />
				</div>
				<div
					className='overflow-hidden'
					data-testid='og-create-summary-product-groups'
				>
					<span className='font-medium'>{t('product_groups')}</span>
					<SummaryList summary={productGroupsSummary} />
				</div>
			</div>
		</div>
	)
}

const SummaryList = ({ summary }: { summary: Summary[] }) => (
	<div className='mt-2 space-y-1'>
		{summary
			.filter((item) => item.statistics.length)
			.map((item) => (
				<p className='ml-2' key={item.name}>
					{item.name}: {item.statistics.map(({ name }) => name).join(', ')}
				</p>
			))}
	</div>
)
